import React from 'react';
import { Loader2 } from 'lucide-react';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    'bg-[#56E8E1] hover:bg-[#34d5cd] text-[#111111] font-bold border border-[#34d5cd]/50 shadow-xs focus:ring-[#1fb4ac]',
  secondary:
    'bg-[#E8FAF9] hover:bg-[#cbf6f3] text-[#1b8e88] font-bold border border-[#74ebe4]/30 focus:ring-[#1fb4ac]',
  outline:
    'bg-white hover:bg-[#F7FAFA] text-[#111111] font-semibold border border-[#E5E9E9] shadow-xs focus:ring-[#1fb4ac]',
  ghost:
    'bg-transparent hover:bg-[#F7FAFA] text-[#5F6368] hover:text-[#111111] font-medium border border-transparent focus:ring-[#1fb4ac]',
  danger:
    'bg-[#FDEDEF] hover:bg-[#fbdbe0] text-[#D94A5C] font-bold border border-[#D94A5C]/20 focus:ring-[#D94A5C]',
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-xs rounded-lg gap-1.5 min-h-[32px]',
  md: 'px-4 py-2.5 text-xs sm:text-sm rounded-xl gap-2 min-h-[40px]',
  lg: 'px-5 py-3 text-sm font-bold rounded-2xl gap-2.5 min-h-[48px]',
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      leftIcon,
      rightIcon,
      fullWidth = false,
      children,
      disabled,
      className = '',
      ...props
    },
    ref
  ) => {
    const isBtnDisabled = disabled || isLoading;

    return (
      <button
        ref={ref}
        disabled={isBtnDisabled}
        className={`inline-flex items-center justify-center transition-all duration-150 cursor-pointer select-none outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none ${
          variantStyles[variant]
        } ${sizeStyles[size]} ${fullWidth ? 'w-full' : ''} ${className}`}
        {...props}
      >
        {isLoading && <Loader2 className="w-4 h-4 animate-spin shrink-0" />}
        {!isLoading && leftIcon && <span className="shrink-0">{leftIcon}</span>}
        {children && <span>{children}</span>}
        {!isLoading && rightIcon && <span className="shrink-0">{rightIcon}</span>}
      </button>
    );
  }
);

Button.displayName = 'Button';

export interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: React.ReactNode;
  'aria-label': string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
}

const iconSizeStyles: Record<ButtonSize, string> = {
  sm: 'p-1.5 rounded-lg min-w-[32px] min-h-[32px]',
  md: 'p-2 rounded-xl min-w-[40px] min-h-[40px]',
  lg: 'p-2.5 rounded-2xl min-w-[48px] min-h-[48px]',
};

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      icon,
      'aria-label': ariaLabel,
      variant = 'ghost',
      size = 'md',
      isLoading = false,
      disabled,
      className = '',
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        aria-label={ariaLabel}
        title={ariaLabel}
        disabled={disabled || isLoading}
        className={`inline-flex items-center justify-center transition-all duration-150 cursor-pointer outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed ${
          variantStyles[variant]
        } ${iconSizeStyles[size]} ${className}`}
        {...props}
      >
        {isLoading ? <Loader2 className="w-4 h-4 animate-spin shrink-0" /> : icon}
      </button>
    );
  }
);

IconButton.displayName = 'IconButton';
